/**
 * @file     canonicalEmail.ts
 * @purpose  Collapse provider-equivalent address spellings to one key so the
 *           same inbox can't be treated as two leads. Gmail ignores dots and
 *           everything after a `+` in the local part, so dot/alias variants
 *           (richard.d.marten vs richarddmarten) are the same mailbox.
 */
const GMAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com']);

/**
 * What a person typed, made submittable. Mobile keyboards autocorrect
 * "gmail.com" into "gmail .com" and pasted addresses carry stray spaces;
 * an address can never legitimately contain whitespace, so every run of it
 * is dropped rather than rejected. Case is folded because mailboxes are
 * case-insensitive in practice and one spelling keeps dedup honest.
 */
export function normalizeEmailInput(raw: string): string {
  return raw.replace(/\s+/g, '').toLowerCase();
}

export function canonicalEmail(raw: string): string {
  const email = raw.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at < 1) return email;
  let local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (GMAIL_DOMAINS.has(domain)) {
    const plus = local.indexOf('+');
    if (plus !== -1) local = local.slice(0, plus);
    local = local.replace(/\./g, '');
    return `${local}@gmail.com`;
  }
  return `${local}@${domain}`;
}
